import { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/feature/Seo";

const SUBMIT_ADDR = "https://readdy.ai/api/form/da263gbk8r5te5mqsi3g";

const contactWays = [
  { icon: "ri-mail-line", title: "电子邮件", value: "hello@sakura-manga.example" },
  { icon: "ri-chat-3-line", title: "官方公众号", value: "樱花漫画" },
  { icon: "ri-bilibili-line", title: "哔哩哔哩", value: "@樱花漫画" },
  { icon: "ri-time-line", title: "回复时间", value: "工作日 9:00 - 18:00" },
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = fd.get("website_alt") as string;
    if (honeypot && honeypot.trim().length > 0) {
      setStatus("success");
      setMessage("提交成功，我们会尽快与你联系！");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key !== "website_alt" && String(value).trim() !== "") {
        payload.append(key, String(value));
      }
    });

    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;
      const isSpam = /spam/i.test(responseText);
      const ok = res.ok && !isSpam && parsed?.code === "OK";

      if (ok) {
        setStatus("success");
        setMessage("提交成功，我们会尽快与你联系！");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交失败，请稍后再试。");
    }
  };

  return (
    <>
      <Seo
        title="联系我们 - 樱花漫画"
        description="与樱花漫画取得联系：商务合作、投稿咨询、意见反馈与问题解答，欢迎通过邮件或表单与我们沟通。"
        canonical="https://comicflower.github.io/contact"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "联系樱花漫画",
          description: "樱花漫画联系与合作渠道。",
        }}
      />
      <div className="mx-auto max-w-7xl px-4 pt-24 md:px-6 md:pt-28">
        <nav className="flex flex-wrap items-center gap-1 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-700">首页</Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <span className="text-foreground-800">联系我们</span>
        </nav>
        <h1 className="mt-4 font-heading text-3xl font-black text-foreground-950 md:text-4xl">联系我们</h1>
        <p className="mt-2 max-w-2xl text-sm text-foreground-600">
          无论是商务合作、投稿咨询还是意见反馈，欢迎随时与我们联系。我们会尽快回复每一条消息。
        </p>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-10 md:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.3fr]">
          <div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {contactWays.map((w) => (
                <div key={w.title} className="rounded-2xl border border-background-200 bg-background-100 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className={`${w.icon} text-lg`} aria-hidden="true"></i>
                  </span>
                  <p className="mt-3 text-xs text-foreground-500">{w.title}</p>
                  <p className="mt-1 text-sm font-medium text-foreground-900">{w.value}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 rounded-2xl border border-background-200 bg-background-100 p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">投稿与合作说明</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                我们欢迎漫评、盘点与创作干货类投稿。请在留言中说明投稿类型，审核通过后将于「资讯」栏目署名发布。
              </p>
            </div>
          </div>

          <form
            data-readdy-form="contact"
            onSubmit={handleSubmit}
            className="rounded-2xl border border-background-200 bg-background-50 p-6 md:p-8"
          >
            <h2 className="font-heading text-lg font-bold text-foreground-950">发送留言</h2>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="text-sm font-medium text-foreground-800">你的称呼</label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  placeholder="怎么称呼你"
                  className="mt-2 w-full rounded-md border border-background-300 bg-background-100 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="text-sm font-medium text-foreground-800">邮箱</label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  placeholder="用于回复你"
                  className="mt-2 w-full rounded-md border border-background-300 bg-background-100 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
              </div>
            </div>
            <div className="mt-4">
              <label htmlFor="contact-topic" className="text-sm font-medium text-foreground-800">留言类型</label>
              <select
                id="contact-topic"
                name="topic"
                className="mt-2 w-full rounded-md border border-background-300 bg-background-100 px-4 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
              >
                <option>一般咨询</option>
                <option>商务合作</option>
                <option>投稿</option>
                <option>意见反馈</option>
              </select>
            </div>
            <div className="mt-4">
              <label htmlFor="contact-message" className="text-sm font-medium text-foreground-800">留言内容</label>
              <textarea
                id="contact-message"
                name="message"
                required
                rows={5}
                maxLength={500}
                placeholder="写下你想说的话（最多 500 字）"
                className="mt-2 w-full resize-none rounded-md border border-background-300 bg-background-100 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>
            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="hp-field"
            />
            <button
              type="submit"
              className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:w-auto"
            >
              提交留言
              <i className="ri-send-plane-fill text-sm" aria-hidden="true"></i>
            </button>
            {status !== "idle" && (
              <p
                className={`mt-4 text-sm ${status === "success" ? "text-secondary-700" : "text-primary-700"}`}
                role="status"
              >
                {message}
              </p>
            )}
          </form>
        </div>
      </div>
    </>
  );
}